/** @jsxImportSource ./simboluri-jsx */
// BARA SECȚIUNII: aprinsul, axa, sensul și cursorul — sus, peste canvas.
//
// Stă DREAPTA-SUS, sub comutatorul de randare, fiindcă spun același fel de
// lucru: nu CE se vede (aia e coloana din stânga, cu layerele), ci CUM te uiți
// la el. Secțiunea nu adaugă și nu scoate nicio familie de piese — e o stare a
// VEDERII, ca zoom-ul. De asta nici nu stă în lista de bife.
//
// Mijlocul de sus pare liber și nu e: acolo stă comutatorul de disciplină al
// Studioului. V. `legacy.css` pentru ce s-a întâmplat când bara a fost pusă
// acolo.
//
// Stinsă, bara e un singur buton: un model pe care nimeni nu-l taie n-are de
// ce să țină un cursor pe ecran. Aprinsă, se desface în tot ce trebuie ca să
// nimerești tăietura din prima:
//
//   * AXA (X, Y, Z) — axele GRILEI, nu ale desenului: pe o clădire rotită,
//     „pe X" taie tot de-a lungul pereților (v. `directiaAxei`);
//   * SENSUL (⇄) — care jumătate rămâne. La aprindere se alege singur, ca
//     tăietura să fie cu fața la om; butonul e pentru cazul în care omul se
//     mută cu camera de cealaltă parte;
//   * CURSORUL — plimbă planul prin clădire. E singurul gest care dă și o
//     poziție exactă: săgețile îl mișcă din centimetru în centimetru,
//     Home/End îl duc la capete (comportarea firească a unui `range`, de
//     asta nici nu se rescrie).
//
// CURSORUL SE PRINDE DE NIVELURI, dar numai la tăietura orizontală: acolo
// structuristul nu vrea „pe la 6,3 m", vrea „prin etajul 2" — aceeași nevoie
// pe care ETABS o rezolvă cu limitarea pe niveluri. Pe X și Y nu există cote
// de care să se prindă, deci nu se prinde de nimic.
//
// Se prinde de MIJLOCUL nivelului, NU de cota plăcii lui. Cota plăcii pare
// alegerea firească și e cea mai proastă: acolo planul taie chiar prin
// planșeu, iar fața tăiată a plăcii e tot nivelul — o tablă opacă întinsă
// peste ecran, care acoperă exact ce voiai să vezi. Tăiat pe la mijlocul
// etajului, prin stâlpi și pereți, ce rămâne dedesubt E planul nivelului.

import { AxaSectiune, Limite, StareSectiune } from "./sectiune3d";
import { t, useT } from "../i18n";
import type { Cheie } from "../i18n";
import { mFmt } from "./cote";

/** Cât de aproape de un nivel trebuie dus cursorul ca să se prindă de el. Un
 *  etaj are 2,8–3,2 m: 12 cm e destul cât să-l nimerești din mișcare, și prea
 *  puțin ca să nu mai poți tăia unde vrei — de pildă chiar prin placă. */
const PRINDERE_M = 0.12;

/** Un nivel și cota la care se taie PRIN el (mijlocul lui), nu cota plăcii. */
export interface NivelSectiune { nivel: string; z_m: number }

/** Nivelul de care se prinde tăietura orizontală, dacă e vreunul aproape. */
export function prindeNivel(t: number, niveluri: NivelSectiune[]) {
  let cel: NivelSectiune | null = null;
  for (const n of niveluri)
    if (Math.abs(n.z_m - t) <= PRINDERE_M
        && (!cel || Math.abs(n.z_m - t) < Math.abs(cel.z_m - t))) cel = n;
  return cel;
}

/** Ce scrie lângă cursor. Pe verticală, COTA modelului (0 = ±0,00, cea de pe
 *  planșe); pe orizontală, cota n-ar spune nimic — coordonatele X/Y sunt ale
 *  desenului importat, deci se măsoară de la marginea clădirii. */
export function textSectiune(s: StareSectiune, limite: Limite,
                             niveluri: NivelSectiune[]): string {
  if (s.axa !== "z") {
    const d = s.t - limite[s.axa].min;
    return `${s.axa.toUpperCase()} + ${mFmt(d)} m`;
  }
  const semn = s.t < -0.005 ? "−" : "+";
  const cota = `${semn}${mFmt(Math.abs(s.t))} m`;
  const n = prindeNivel(s.t, niveluri);
  return n ? t("struct.sect.prin_nivel", { cota, nivel: n.nivel }) : cota;
}

function Pictograma() {
  // o cutie tăiată de un plan: jumătatea de dincoace lipsește, fața tăiată e
  // plină. Aceeași poveste ca pe ecran, în 16 px.
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
      <path d="M2.5 6.2 8 3.4l5.5 2.8v4.2L8 13.2 2.5 10.4Z"
            fill="none" stroke="currentColor" strokeWidth="1"
            strokeLinejoin="round" opacity=".45" strokeDasharray="1.6 1.4" />
      <path d="M8 3.4l5.5 2.8v4.2L8 13.2Z" fill="currentColor" opacity=".18" />
      <path d="M8 3.4 8 13.2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

const TITLU: Record<AxaSectiune, Cheie> = {
  x: "struct.sect.titlu_x",
  y: "struct.sect.titlu_y",
  z: "struct.sect.titlu_z",
};

export default function BaraSectiune({
  stare, onStare, limite, niveluri, inversImplicit,
}: {
  stare: StareSectiune | null;
  onStare: (s: StareSectiune | null) => void;
  /** capetele cursorului pe fiecare axă; lipsesc până se construiește scena */
  limite: Limite | null;
  niveluri: NivelSectiune[];
  /** ce jumătate se aruncă la aprinderea pe axa asta: cea dinspre cameră */
  inversImplicit: (axa: AxaSectiune) => boolean;
}) {
  const tr = useT();
  if (!limite) return null;

  // la aprindere, planul pleacă din MIJLOCUL clădirii pe axa lui: de acolo se
  // vede din prima că s-a tăiat ceva. Pornit de la un capăt, primul lucru pe
  // care l-ar face omul ar fi să caute cu cursorul clădirea.
  const porneste = (axa: AxaSectiune) => onStare({
    axa,
    t: (limite[axa].min + limite[axa].max) / 2,
    invers: inversImplicit(axa),
  });

  if (!stare) {
    return (
      <div className="fem-sectiune">
        <button
          type="button"
          className="fem-sect-buton"
          title={tr("struct.sect.aprinde_titlu")}
          onClick={() => porneste("z")}
        >
          <Pictograma /> {tr("struct.sect.buton")}
        </button>
      </div>
    );
  }

  const lim = limite[stare.axa];
  return (
    <div className="fem-sectiune on">
      <button type="button" className="fem-sect-buton on"
              title={tr("struct.sect.stinge")}
              onClick={() => onStare(null)}>
        <Pictograma /> {tr("struct.sect.buton")}
      </button>
      <span className="fem-sect-axe" role="group" aria-label={tr("struct.sect.axa_grup")}>
        {(["x", "y", "z"] as AxaSectiune[]).map((a) => (
          <button key={a} type="button" title={tr(TITLU[a])}
                  aria-pressed={a === stare.axa}
                  className={a === stare.axa ? "on" : ""}
                  onClick={() => porneste(a)}>
            {a.toUpperCase()}
          </button>
        ))}
      </span>
      <button type="button" className="fem-sect-intoarce"
              title={tr("struct.sect.intoarce")}
              onClick={() => onStare({ ...stare, invers: !stare.invers })}>
        ⇄
      </button>
      <input
        type="range"
        className="fem-sect-cursor"
        aria-label={tr("struct.sect.cursor")}
        min={lim.min} max={lim.max} step={0.01}
        value={stare.t}
        list={stare.axa === "z" ? "fem-sect-niveluri" : undefined}
        onChange={(e) => {
          const brut = Number(e.target.value);
          const n = stare.axa === "z" ? prindeNivel(brut, niveluri) : null;
          onStare({ ...stare, t: n ? n.z_m : brut });
        }}
      />
      {stare.axa === "z" && (
        <datalist id="fem-sect-niveluri">
          {niveluri.map((n) => (
            <option key={`${n.nivel}-${n.z_m}`} value={n.z_m} label={n.nivel} />
          ))}
        </datalist>
      )}
      <span className="fem-sect-cota">
        {textSectiune(stare, limite, niveluri)}
      </span>
    </div>
  );
}
